import { assetDictionary } from "@dinahmoe/dmafjs-assetloader";
import { visualEntities } from "../entities.js";
import { create } from "../helpers/elements.helper.js";
import { VisualSuperHtml } from "./visual-super.environment.js";
import { addToPendingTransforms, cssValue } from "../helpers/styles.helper.js";

class ContainerHtml extends VisualSuperHtml {
  handlers = this.extendParentHandlers({
    "container.maskImage": this.setMaskImage,
    "container.maskWidth": this.setMaskImageWidth,
    "scroll.x": this.setScrollX,
    "scroll.y": this.setScrollY,
    "shadow.x": this.setBoxShadow,
    "shadow.y": this.setBoxShadow,
    "shadow.blur": this.setBoxShadow,
    "shadow.color": this.setBoxShadow,
    "container.overflow": this.containerOverflow,
  });

  setProps(eNode) {
    eNode.element = create(eNode, "div");
    super.setProps(eNode);
  }

  _enableFlex(eNode) {
    if (!eNode.hasFlex) {
      eNode.hasFlex = true;
      eNode.element.style.display = "flex";
    }
  }

  layoutDirection(eNode) {
    const value = eNode.props["layout.direction"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.flexDirection = value;
  }

  layoutWrap(eNode) {
    const value = eNode.props["layout.wrap"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.flexWrap = value;
  }

  layoutJustifyContent(eNode) {
    const value = eNode.props["layout.justifyContent"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.justifyContent = value;
  }

  layoutAlignItems(eNode) {
    const value = eNode.props["layout.alignItems"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.alignItems = value;
  }

  layoutAlignContent(eNode) {
    const value = eNode.props["layout.alignContent"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.alignContent = value;
  }

  layoutGap(eNode) {
    const value = eNode.props["layout.gap"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.gap = cssValue(value);
  }

  layoutGapRow(eNode) {
    const value = eNode.props["layout.gapRow"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.rowGap = cssValue(value);
  }

  layoutGapColumn(eNode) {
    const value = eNode.props["layout.gapColumn"];
    if (value === undefined) return;
    this._enableFlex(eNode);
    eNode.element.style.columnGap = cssValue(value);
  }

  paddingTop(eNode) {
    const value = eNode.props["padding.top"];
    if (value === undefined) return;
    eNode.element.style.paddingTop = cssValue(value);
  }

  paddingRight(eNode) {
    const value = eNode.props["padding.right"];
    if (value === undefined) return;
    eNode.element.style.paddingRight = cssValue(value);
  }

  paddingBottom(eNode) {
    const value = eNode.props["padding.bottom"];
    if (value === undefined) return;
    eNode.element.style.paddingBottom = cssValue(value);
  }

  paddingLeft(eNode) {
    const value = eNode.props["padding.left"];
    if (value === undefined) return;
    eNode.element.style.paddingLeft = cssValue(value);
  }

  containerOverflow(eNode) {
    eNode.originalPointerEvents ??= eNode.element.style.pointerEvents;

    if (eNode.props["container.overflow"] === "visible") {
      eNode.element.style.overflow = "visible";
      eNode.element.style.pointerEvents = eNode.originalPointerEvents;
      return;
    }

    if (eNode.props["container.overflow"] === "hidden") {
      eNode.element.style.overflow = "hidden";
      eNode.element.style.pointerEvents = eNode.originalPointerEvents;
      return;
    }

    if (eNode.props["container.overflow"] === "scroll") {
      eNode.element.style.overflow = "scroll";
      eNode.element.style.pointerEvents = "auto";
      return;
    }

    if (eNode.props["container.overflow"] === "scroll-x") {
      eNode.element.style.overflowX = "scroll";
      eNode.element.style.overflowY = "hidden";
      eNode.element.style.pointerEvents = "auto";
      return;
    }

    if (eNode.props["container.overflow"] === "scroll-y") {
      eNode.element.style.overflowX = "hidden";
      eNode.element.style.overflowY = "scroll";
      eNode.element.style.pointerEvents = "auto";
    }
  }

  setMaskImage(eNode) {
    const maskImage = eNode.props["container.maskImage"];
    if (maskImage === undefined) {
      return;
    }

    const maskImageWidth = eNode.props["container.maskWidth"];
    const maskImageElement = assetDictionary.get(`assets.image.${maskImage}`);
    const maskImageSource = maskImageElement?.src;
    eNode.element.style["-webkit-mask-size"] = eNode.element.style["mask-size"] =
      `${maskImageWidth}px` ?? "cover";
    eNode.element.style["-webkit-mask-repeat"] = eNode.element.style["mask-repeat"] = "no-repeat";
    eNode.element.style["-webkit-mask-position"] = eNode.element.style["mask-position"] = "center";
    eNode.element.style["-webkit-mask-image"] = eNode.element.style["mask-image"] =
      maskImage === "" || maskImage === "none" ? "none" : `url(${maskImageSource})`;

    addToPendingTransforms(eNode);
  }

  setMaskImageWidth(eNode) {
    const value = eNode.props["container.maskWidth"];

    eNode.element.style["-webkit-mask-size"] = eNode.element.style["mask-size"] =
      `${value}px` ?? "cover";
  }

  setScrollX(eNode) {
    eNode.element.scrollLeft = eNode.props["scroll.x"];
  }

  setScrollY(eNode) {
    eNode.element.scrollTop = eNode.props["scroll.y"];
  }

  setBoxShadow(eNode) {
    const shadowOffsetX = eNode.props["shadow.x"];
    const shadowOffsetY = eNode.props["shadow.y"];
    const shadowBlur = eNode.props["shadow.blur"];
    const shadowColor = eNode.props["shadow.color"];

    if (shadowColor === undefined) {
      eNode.element.style.boxShadow = "none";
    } else {
      const offsetX = shadowOffsetX !== undefined ? shadowOffsetX : 0;
      const offsetY = shadowOffsetY !== undefined ? shadowOffsetY : 0;
      const blur = shadowBlur !== undefined ? shadowBlur : 0;
      eNode.element.style.boxShadow = `${offsetX}px ${offsetY}px ${blur}px ${shadowColor}`;
    }
  }
}

const containerHtml = new ContainerHtml("visual:container");
visualEntities.set("visual:container", containerHtml);
visualEntities.set("visual:group", containerHtml);
visualEntities.set("visual:widget", containerHtml);
visualEntities.set("visual:shape", containerHtml);
export { ContainerHtml, containerHtml };
